import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { Download, Pencil, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { TimeseriesChart } from "@/components/ui/chart"
import { Input } from "@/components/ui/input"
import {
  countdown,
  type DirectiveDetail,
  type DirectiveLine,
  type DirectiveStatus,
  directiveStatusLabel,
  directiveStatusTone,
  directiveTypeLabel,
  formatDate,
  formatQtyCompact,
  formatRpCompact,
  type LineStatus,
  lineStatusLabel,
  lineStatusTone,
  trajectoryMetricLabel,
} from "@/lib/accountDirectives"

type PageProps = {
  directive: DirectiveDetail
}

export default function Show({ directive }: PageProps) {
  const title = directive.ref_no ?? "Peringatan tanpa nomor"
  const description = `Peringatan ${title} untuk akun ${directive.account_name ?? "—"}: ${directive.line_count} baris SKU, tenggat, dan dokumen sumber.`

  const destroy = () => {
    if (!window.confirm("Hapus peringatan ini beserta seluruh baris SKU dan dokumennya?")) return
    router.delete(`/admin/accounts/directives/${directive.id}`)
  }

  const transitionStatus = (status: DirectiveStatus) => {
    router.patch(`/admin/accounts/directives/${directive.id}/transition`, { status })
  }

  const deadlineCountdown = countdown(directive.grace_deadline)

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <Link href="/admin/accounts" className="hover:text-ink-body">
            Account
          </Link>
          <span>/</span>
          <Link href="/admin/accounts/directives" className="hover:text-ink-body">
            Peringatan
          </Link>
          <span>/</span>
          <span className="text-ink-body font-medium">{title}</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1>{title}</h1>
                <Badge tone="neutral">{directiveTypeLabel(directive.directive_type)}</Badge>
                <Badge tone={directiveStatusTone(directive.status)}>
                  {directiveStatusLabel(directive.status)}
                </Badge>
                {directive.status === "active" && deadlineCountdown && (
                  <Badge tone={deadlineCountdown.tone}>Grace: {deadlineCountdown.label}</Badge>
                )}
              </div>
              {directive.account_name && (
                <p className="mt-1 text-sm text-ink-muted">
                  <Link href={`/admin/accounts/${directive.account_id}`} className="hover:text-ink-body">
                    {directive.account_name}
                  </Link>
                  {directive.brand ? ` · ${directive.brand}` : ""}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button asChild variant="secondary">
                <Link href={`/admin/accounts/directives/${directive.id}/edit`}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Link>
              </Button>
              <Button variant="danger" onClick={destroy}>
                <Trash2 className="mr-2 h-4 w-4" />
                Hapus
              </Button>
            </div>
          </div>
        </div>

        {/* Status lifecycle control (M3) */}
        {directive.allowed_transitions.length > 0 && (
          <section className="mt-6 rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">Ubah status peringatan</div>
            <p className="mt-1 text-sm text-ink-body">
              Status saat ini: <strong>{directiveStatusLabel(directive.status)}</strong>. Pilih status berikutnya:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {directive.allowed_transitions.map((next) => (
                <Button
                  key={next}
                  variant={next === "cancelled" ? "secondary" : "primary"}
                  onClick={() => transitionStatus(next)}
                >
                  {directiveStatusLabel(next)}
                </Button>
              ))}
            </div>
          </section>
        )}

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Memo header */}
          <section className="rounded-lg border border-hairline bg-surface p-5 lg:col-span-2">
            <div className="text-sm font-medium text-ink-muted">Detail Memo</div>
            <dl className="mt-4 grid gap-x-6 gap-y-4 sm:grid-cols-2">
              <Field label="Subjek" value={directive.subject} />
              <Field label="Brand" value={directive.brand} />
              <Field label="Entitas supplier" value={directive.supplier_entity} />
              <Field label="PIC supplier (Up.)" value={directive.supplier_pic} />
              <Field label="Tanggal terbit" value={formatDate(directive.issued_on)} />
              <Field label="Dicatat oleh" value={directive.captured_by_name} />
            </dl>

            {directive.signatories.length > 0 && (
              <div className="mt-5 border-t border-hairline pt-4">
                <div className="text-xs font-medium text-ink-muted">Penanda tangan (sisi akun)</div>
                <ul className="mt-2 flex flex-col gap-1 text-sm text-ink-body">
                  {directive.signatories.map((s, i) => (
                    <li key={i}>
                      {s.name}
                      {s.role ? ` — ${s.role}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Consequence timeline + document */}
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="text-sm font-medium text-ink-muted">Tenggat & Konsekuensi</div>
            <dl className="mt-4 flex flex-col gap-3">
              <Field label="Masa tenggang (grace)" value={formatDate(directive.grace_deadline)} />
              <Field label="Tinjauan" value={formatDate(directive.review_on)} />
              <Field label="Flush-out" value={formatDate(directive.flush_out_on)} />
              <Field label="Discontinue" value={formatDate(directive.discontinue_on)} />
            </dl>

            <div className="mt-5 border-t border-hairline pt-4">
              <div className="text-xs font-medium text-ink-muted">Dokumen sumber</div>
              {directive.document_url ? (
                <a
                  href={directive.document_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
                >
                  <Download className="h-4 w-4" />
                  {directive.document_filename ?? "Unduh dokumen"}
                </a>
              ) : (
                <p className="mt-2 text-sm text-ink-muted">Tidak ada dokumen terlampir.</p>
              )}
            </div>
          </section>
        </div>

        {/* Clear conditions + consequence summary */}
        {(directive.clear_conditions || directive.consequence_summary) && (
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {directive.clear_conditions && (
              <section className="rounded-lg border border-hairline bg-surface p-5">
                <div className="text-sm font-medium text-ink-muted">Syarat pemulihan</div>
                <p className="mt-2 whitespace-pre-line text-sm text-ink-body">
                  {directive.clear_conditions}
                </p>
              </section>
            )}
            {directive.consequence_summary && (
              <section className="rounded-lg border border-hairline bg-surface p-5">
                <div className="text-sm font-medium text-ink-muted">Ringkasan konsekuensi</div>
                <p className="mt-2 whitespace-pre-line text-sm text-ink-body">
                  {directive.consequence_summary}
                </p>
              </section>
            )}
          </div>
        )}

        {/* SKU lines */}
        <section className="mt-6">
          <h2 className="text-base font-semibold text-ink-display">
            Baris SKU ({directive.lines.length})
          </h2>
          <div className="mt-4 flex flex-col gap-3">
            {directive.lines.map((line) => (
              <article
                key={line.id ?? line.raw_product_description}
                className="rounded-lg border border-hairline bg-surface p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-medium text-ink-body">{line.raw_product_description}</div>
                    <div className="mt-0.5 text-xs text-ink-muted">
                      {line.account_sku_code && <span>PLU/SKU: {line.account_sku_code}</span>}
                      {line.account_sku_code && line.product_code && <span> · </span>}
                      {line.product_code && <span>STAR: {line.product_code}</span>}
                    </div>
                  </div>
                  <Badge tone={lineStatusTone(line.line_status)}>
                    {lineStatusLabel(line.line_status)}
                  </Badge>
                </div>

                {(line.threshold_value || Object.keys(line.metrics).length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 border-t border-hairline pt-3 text-sm">
                    {line.threshold_value && (
                      <div>
                        <span className="text-xs text-ink-muted">Threshold: </span>
                        <span className="text-ink-body">
                          {line.threshold_value}
                          {line.threshold_unit ? ` ${line.threshold_unit}` : ""}
                        </span>
                      </div>
                    )}
                    {Object.entries(line.metrics).map(([k, v]) => (
                      <div key={k}>
                        <span className="text-xs text-ink-muted">{k}: </span>
                        <span className="text-ink-body">{String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <LineStatusControl directiveId={directive.id} line={line} />

                <LineTrajectory line={line} />
              </article>
            ))}
          </div>
        </section>
      </AppShell>
    </>
  )
}

function LineStatusControl({ directiveId, line }: { directiveId: number; line: DirectiveLine }) {
  const [pending, setPending] = useState<LineStatus | null>(null)
  const [outcome, setOutcome] = useState("")

  // Resolved lines (or unsaved rows) carry no further transitions.
  if (line.id == null || !line.allowed_transitions || line.allowed_transitions.length === 0) {
    if (line.outcome) {
      return (
        <div className="mt-3 border-t border-hairline pt-3 text-xs text-ink-muted">
          Hasil: {line.outcome}
          {line.resolved_on ? ` · ${formatDate(line.resolved_on)}` : ""}
        </div>
      )
    }
    return null
  }

  const submit = (status: LineStatus) => {
    router.patch(`/admin/accounts/directives/${directiveId}/transition`, {
      line_id: line.id,
      line_status: status,
      outcome: outcome || undefined,
    })
  }

  return (
    <div className="mt-3 border-t border-hairline pt-3">
      {pending ? (
        <div className="flex flex-col gap-2">
          <label htmlFor={`outcome-${line.id}`}>Catatan hasil (opsional)</label>
          <Input
            id={`outcome-${line.id}`}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            placeholder="mis. SKU pulih di atas threshold pada Mei 2026"
          />
          <div className="flex gap-2">
            <Button onClick={() => submit(pending)}>
              Konfirmasi: {lineStatusLabel(pending)}
            </Button>
            <Button variant="secondary" onClick={() => setPending(null)}>
              Batal
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-muted">Selesaikan baris:</span>
          {line.allowed_transitions.map((next) => (
            <Button key={next} variant="secondary" onClick={() => setPending(next)}>
              Tandai {lineStatusLabel(next)}
            </Button>
          ))}
        </div>
      )}
    </div>
  )
}

function LineTrajectory({ line }: { line: DirectiveLine }) {
  const trajectory = line.trajectory

  // Unmapped (or no data) lines render a gentle hint instead of a chart.
  if (!trajectory || !trajectory.available || trajectory.series.length === 0) {
    return (
      <div className="mt-3 border-t border-hairline pt-3 text-xs text-ink-muted">
        {line.product_code
          ? "Belum ada data sell-out untuk SKU ini pada akun terkait."
          : "Petakan SKU ke master STAR untuk melihat tren performa vs. threshold."}
      </div>
    )
  }

  const isQty = trajectory.unit_metric === "qty"
  const formatter = isQty ? formatQtyCompact : formatRpCompact

  const chartData = trajectory.series.map((point) => ({
    label: point.label,
    value: isQty ? point.qty : point.value,
  }))

  return (
    <div className="mt-4 border-t border-hairline pt-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-xs font-medium text-ink-muted">
          Tren performa vs. threshold · {trajectoryMetricLabel(trajectory.unit_metric)}
        </div>
        <div className="text-xs text-ink-muted">
          STAR {trajectory.product_code} · scope {trajectory.scope_value ?? "—"}
        </div>
      </div>

      <TimeseriesChart
        className="mt-3"
        height={260}
        data={chartData}
        series={[{ key: "value", name: trajectoryMetricLabel(trajectory.unit_metric) }]}
        valueFormatter={formatter}
        referenceValue={trajectory.threshold_value}
        referenceLabel={trajectory.threshold_value != null ? "Ambang batas" : undefined}
      />

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[28rem] text-sm">
          <thead>
            <tr className="border-b border-hairline text-left text-xs text-ink-muted">
              <th className="py-1.5 pr-3 font-medium">Periode</th>
              <th className="py-1.5 pr-3 text-right font-medium">Netto (Rp)</th>
              <th className="py-1.5 text-right font-medium">Qty (pcs)</th>
            </tr>
          </thead>
          <tbody>
            {trajectory.series.map((point) => (
              <tr key={point.label} className="border-b border-hairline/60">
                <td className="py-1.5 pr-3 text-ink-body tabular-nums">{point.label}</td>
                <td className="py-1.5 pr-3 text-right text-ink-body tabular-nums">
                  {point.value == null ? "—" : new Intl.NumberFormat("id-ID").format(point.value)}
                </td>
                <td className="py-1.5 text-right text-ink-body tabular-nums">
                  {point.qty == null ? "—" : new Intl.NumberFormat("id-ID").format(point.qty)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5 text-ink-body">{value && value !== "—" ? value : "—"}</dd>
    </div>
  )
}
